// BaseDisplayItem.tsx
import React from 'react'
import Kedo from '../../Kedo.js'

export type DisplayMode =
    | 'show'
    | 'edit'
    | 'show/edit'
    | 'search'
    | 'advanced-search'

export type DisplayErrors = string | string[] | undefined

export interface DisplayItemBase {
    id: string | number
    // commonly used settings
    settings?: Record<string, unknown>

    // optional nested def-field settings (as in your codebase)
    def_dossier_def_field?: {
        settings?: Record<string, unknown>
        def_field?: {
            settings?: Record<string, unknown>
        }
    }
}

export interface BaseDisplayItemProps<
    TValue = string,
    TItem extends DisplayItemBase = DisplayItemBase
> {
    kedo: Kedo
    value?: TValue
    mode: DisplayMode
    errors?: DisplayErrors
    item: TItem
    changeValue?: (id: TItem['id'], value: TValue) => void
}

export interface BaseDisplayItemState<TValue = string> {
    value: TValue
}

export default abstract class BaseDisplayItem<
    TValue = string,
    TItem extends DisplayItemBase = DisplayItemBase,
    R extends HTMLElement = HTMLElement,
    TState extends BaseDisplayItemState<TValue> = BaseDisplayItemState<TValue>
> extends React.Component<BaseDisplayItemProps<TValue, TItem>, TState> {
    protected innerRef: React.RefObject<R | null> = React.createRef<R | null>()
    kedo: Kedo

    constructor(props: BaseDisplayItemProps<TValue, TItem>) {
        super(props)

        this.state = {
            value: props.value as TValue,
        } as TState
    }

    /* ---------- Common helpers ---------- */

    doFocus() {
        setTimeout(() => this.innerRef.current?.focus(), 1)
        if (this.innerRef.current) {
            window.scrollTo(0, (this.innerRef.current as any).offsetTop ?? 0)
        }
    }

    getValue(): TValue {
        return this.state.value
    }

    isInvalid(): boolean {
        const { errors } = this.props
        return Boolean(
            errors &&
                (Array.isArray(errors) ? errors.length : String(errors).length)
        )
    }

    getErrorText(separator = ', '): string {
        const { errors } = this.props
        if (!errors) return ''
        return Array.isArray(errors) ? errors.join(separator) : errors
    }

    protected getSetting<T = unknown>(
        key: string,
        defaultValue?: T
    ): T | undefined {
        const fromItem = this.props.item.settings?.[key] as T | undefined
        const fromDef = this.props.item.def_dossier_def_field?.def_field
            ?.settings?.[key] as T | undefined

        return fromItem ?? fromDef ?? defaultValue
    }

    updateValue(value: TValue) {
        this.setState({ value })
        this.props.changeValue?.(this.props.item.id, value)
    }

    /* ---------- Lifecycle ---------- */

    componentDidUpdate(prevProps: BaseDisplayItemProps<TValue, TItem>) {
        // Keep local state in sync if parent value changes
        if (prevProps.value !== this.props.value) {
            this.setState({ value: this.props.value as TValue })
            return
        }

        // Master-equivalent (legacy Components/DisplayItems/TextField.jsx): when
        // leaving edit or show/edit for show, realign local state with props.value.
        // Parent dossier `content` is not updated on each keystroke, so props.value
        // stays canonical while local state may still hold unsaved edits.
        const leftEditLike =
            prevProps.mode === 'edit' || prevProps.mode === 'show/edit'
        const nowShow = this.props.mode === 'show'
        if (leftEditLike && nowShow && this.state.value !== this.props.value) {
            this.setState({ value: this.props.value as TValue })
        }
    }

    /* ---------- To be implemented by subclasses ---------- */

    abstract renderShow(): React.ReactNode
    abstract renderEdit(): React.ReactNode
    abstract renderSearch(): React.ReactNode

    /* ---------- Mode switch ---------- */

    render(): React.ReactNode {
        const { mode } = this.props

        if (mode === 'search' || mode === 'advanced-search') {
            return this.renderSearch()
        }

        if (mode === 'edit' || mode === 'show/edit') {
            return this.renderEdit()
        }

        return this.renderShow()
    }
}
